import React, { useState } from 'react';
import { submitBugReport } from '../../api/messagingApi';
import './BugReportModal.css';

/**
 * BugReportModal - Modal for submitting bug reports
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 */
function BugReportModal({ isOpen, onClose }) {
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // VALIDATE PROPS - NO SILENT FAILURES
    if (typeof isOpen !== 'boolean') {
        throw new Error('BugReportModal: isOpen prop must be a boolean');
    }
    if (typeof onClose !== 'function') {
        throw new Error('BugReportModal: onClose prop must be a function');
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate description length
        if (description.length < 10) {
            setError('Please provide at least 10 characters describing the issue.');
            return;
        }

        if (description.length > 2000) {
            setError('Description must be 2000 characters or less.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Auto-capture user agent and page URL
            const userAgent = navigator.userAgent;
            const pageUrl = window.location.pathname;

            await submitBugReport(description, userAgent, pageUrl);
            
            console.log('BugReportModal: Bug report submitted successfully');
            setSuccess(true);
            setDescription('');

            // Auto-close after 2 seconds
            setTimeout(() => {
                setSuccess(false);
                onClose();
            }, 2000);
        } catch (err) {
            console.error('BugReportModal: Error submitting bug report:', err);
            setError(err.message || 'Failed to submit bug report. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            onClose();
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setDescription('');
            setError(null);
            setSuccess(false);
            onClose();
        }
    };

    if (!isOpen) {
        return null;
    }

    const remainingChars = 2000 - description.length;

    return (
        <div className="bug-report-modal-overlay" onClick={handleOverlayClick}>
            <div className="bug-report-modal">
                {/* Header */}
                <div className="bug-report-modal-header">
                    <h2 className="bug-report-modal-title">Report a Bug</h2>
                    <button 
                        className="bug-report-modal-close" 
                        onClick={handleClose}
                        disabled={isSubmitting}
                        aria-label="Close"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="bug-report-modal-body">
                    {success ? (
                        <div className="bug-report-success">
                            <div className="success-icon">✓</div>
                            <h3 className="success-title">Thank you!</h3>
                            <p className="success-message">
                                Thank you for helping us improve Mythic Football!
                            </p>
                            <p className="success-submessage">
                                Your bug report has been submitted to our team.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="bug-report-form">
                            <div className="bug-report-field">
                                <label htmlFor="bug-description" className="bug-report-label">
                                    Describe the issue *
                                </label>
                                <textarea
                                    id="bug-description"
                                    className="bug-report-textarea"
                                    placeholder="Please describe what went wrong, what you expected to happen, and any steps to reproduce the issue..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={isSubmitting}
                                    rows={8}
                                    autoFocus
                                />
                                <div className="bug-report-char-count">
                                    <span className={remainingChars < 0 ? 'char-count-error' : ''}>
                                        {remainingChars} characters remaining
                                    </span>
                                </div>
                            </div>

                            {error && (
                                <div className="bug-report-error">
                                    {error}
                                </div>
                            )}

                            <div className="bug-report-info">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                                <span>
                                    We'll automatically include your browser info and current page to help us debug.
                                </span>
                            </div>

                            <div className="bug-report-actions">
                                <button
                                    type="button"
                                    className="bug-report-button bug-report-cancel-button"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bug-report-button bug-report-submit-button"
                                    disabled={isSubmitting || description.length < 10 || description.length > 2000}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Report'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BugReportModal;
